/**
 * Shared fresh-snow machinery: decay, quality from wet-bulb, preservation,
 * wind transport, plus corn and breakable-crust detection.
 *
 * Extracted from offpiste-scoring-v5 so the touring scorer can use the same
 * physics with its own elevation model. The off-piste module assumes a fixed
 * surface at station + 350m; these functions instead take an explicit
 * `lapseAdjC` (degrees to SUBTRACT from station temp to get surface temp,
 * negative when the surface sits below the station) and an explicit surface
 * elevation. Behaviour is identical to the original off-piste internals when
 * called with the off-piste constants.
 */

import type { NormalisedHour, WeatherHourly } from "./daily-obs-types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LookbackHour = {
  hoursFromStart: number;
  w: WeatherHourly;
};

/** Wind speed (km/h) at the snow surface, per hour. */
export type WindAccessor = (w: WeatherHourly) => number;

export type CrustRisk = {
  detected: boolean;
  severity: number;
  label: string | null;
  detail: string | null;
  maxDaytimeTempC: number | null;
  minOvernightTempC: number | null;
  hoursSinceLastCoverage: number | null;
};

export type EffectiveFreshResult = {
  effectiveFreshCm: number;
  rawFreshCm: number;
  windDegradationApplied: boolean;
  qualityValues: number[];
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CRUST_COVERAGE_THRESHOLD_CM = 5;

const defaultWind: WindAccessor = (w) => w.wind_speed_80m_kmh ?? 0;

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

function lerp(
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number,
): number {
  if (inMax === inMin) return outMin;
  const t = clamp((value - inMin) / (inMax - inMin), 0, 1);
  return outMin + t * (outMax - outMin);
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ---------------------------------------------------------------------------
// Lookback array construction
// ---------------------------------------------------------------------------

export function buildLookback(
  currentHours: NormalisedHour[],
  previousHours: NormalisedHour[] | undefined,
): LookbackHour[] {
  const result: LookbackHour[] = [];
  if (previousHours) {
    for (const h of previousHours) {
      result.push({ hoursFromStart: h.hour, w: h.weather });
    }
  }
  for (const h of currentHours) {
    result.push({ hoursFromStart: 24 + h.hour, w: h.weather });
  }
  return result;
}

function hoursAgoFrom(lookbackHour: LookbackHour, anchorHoursFromStart: number): number {
  return Math.max(0, anchorHoursFromStart - lookbackHour.hoursFromStart);
}

// ---------------------------------------------------------------------------
// Effective fresh snow
//
// Quality-adjusted, decay-weighted, wind-penalised effective fresh snow.
// ---------------------------------------------------------------------------

function snowQualityFromWetBulb(wetBulbC: number | null, tempC: number, lapseAdjC: number): number {
  const wb = wetBulbC ?? (tempC - 2);
  const adjWb = wb - lapseAdjC;
  if (adjWb <= -12) return 1.05;
  if (adjWb <= -6) return lerp(adjWb, -12, -6, 1.05, 1.0);
  if (adjWb <= -2) return lerp(adjWb, -6, -2, 1.0, 0.80);
  if (adjWb <= 0) return lerp(adjWb, -2, 0, 0.80, 0.45);
  if (adjWb <= 1) return lerp(adjWb, 0, 1, 0.45, 0.20);
  if (adjWb <= 2) return lerp(adjWb, 1, 2, 0.20, 0.10);
  return 0.10;
}

function temperatureHalfLife(meanTempC: number): number {
  if (meanTempC <= -12) return 22;
  if (meanTempC <= -5) return lerp(meanTempC, -12, -5, 22, 18);
  if (meanTempC <= 0) return lerp(meanTempC, -5, 0, 18, 12);
  return lerp(meanTempC, 0, 5, 12, 8);
}

function computePreservationSince(
  snowfallIdx: number,
  lookback: LookbackHour[],
  lapseAdjC: number,
  windKmhOf: WindAccessor,
  anchorHoursFromStart: number,
): number {
  let rainHours = 0;
  let warmHours = 0;
  let windHours = 0;

  // Only hours between the snowfall and the anchor degrade preservation:
  // weather that has not happened yet (relative to the hour being scored)
  // cannot have damaged the snow
  for (let i = snowfallIdx + 1; i < lookback.length; i++) {
    if (lookback[i].hoursFromStart > anchorHoursFromStart) break;
    const w = lookback[i].w;
    if ((w.rain_mm ?? 0) > 0.1) rainHours++;
    if ((w.temp_c - lapseAdjC) > 1.0) warmHours++;
    if (windKmhOf(w) > 30) windHours++;
  }

  const raw =
    Math.pow(0.12, rainHours) *
    Math.pow(0.88, warmHours) *
    Math.pow(0.96, windHours);
  return Math.max(0.05, raw);
}

// P7-17 off-piste wind triple-application (audit 12 B4): DECIDED NO CHANGE (Josh's
// call). Wind appears in three places - this windOnSnowPenalty at the SNOWFALL hour
// (transport loss as the snow is deposited), computePreservationSince's 0.96^windHours
// over the hours AFTER each deposit (erosion of already-fallen snow), and the hourly
// windFactorCurve in the operating-hours score (today's rider comfort). They are
// physically distinct and never charge the same wind value twice: a given hour's wind
// is App1 only for its own deposit (computePreservationSince starts at snowfallIdx+1,
// so it never re-counts the deposit hour's own wind) and App2 only for earlier
// deposits. The audit's "triple" names three real mechanisms, not a same-cause
// double-charge. The residual concern (no shelter/terrain modulation, so chronically
// windy but sheltered freeride zones may be under-scored) is a shelter-DATA limitation
// - off-piste has no per-zone shelter input - not a scoring de-dup; logged for Phase 10.
function windOnSnowPenalty(windKmh: number): number {
  if (windKmh <= 30) return 1.0;
  return Math.max(0.35, 1.0 - (windKmh - 30) * 0.013);
}

/**
 * Quality-adjusted effective fresh snow (cm) over the 48h lookback,
 * as seen from `anchorHoursFromStart`. `surfaceElevM` is the elevation
 * where the snow sits (used for the freezing-level exclusion);
 * `meanTempC` should be the surface temperature driving decay.
 */
export function computeEffectiveFresh(
  lookback: LookbackHour[],
  anchorHoursFromStart: number,
  surfaceElevM: number,
  meanTempC: number,
  lapseAdjC: number,
  windKmhOf: WindAccessor = defaultWind,
): EffectiveFreshResult {
  const halfLife = temperatureHalfLife(meanTempC);
  let totalEffective = 0;
  let rawFresh = 0;
  let windDegradationApplied = false;
  const qualityValues: number[] = [];

  for (let i = 0; i < lookback.length; i++) {
    const h = lookback[i];
    const snowfall = h.w.snowfall_cm ?? 0;
    if (snowfall <= 0) continue;

    // Snow that has not fallen yet (relative to the anchor hour) does not
    // exist on the ground: a morning hour must not be credited with an
    // afternoon dump
    if (h.hoursFromStart > anchorHoursFromStart) continue;

    const hrsAgo = hoursAgoFrom(h, anchorHoursFromStart);
    if (hrsAgo > 48) continue;

    const freezing = h.w.freezing_level_m;
    if (freezing !== null && freezing > surfaceElevM + 200) continue;

    rawFresh += snowfall;

    const decay = Math.exp(-0.693 * hrsAgo / halfLife);
    const quality = snowQualityFromWetBulb(h.w.wet_bulb_temp_c, h.w.temp_c, lapseAdjC);
    qualityValues.push(quality);
    const preservation = computePreservationSince(i, lookback, lapseAdjC, windKmhOf, anchorHoursFromStart);

    const windPen = windOnSnowPenalty(windKmhOf(h.w));
    if (windPen < 1.0) windDegradationApplied = true;

    totalEffective += snowfall * decay * quality * preservation * windPen;
  }

  return { effectiveFreshCm: totalEffective, rawFreshCm: rawFresh, windDegradationApplied, qualityValues };
}

// ---------------------------------------------------------------------------
// Corn Snow Detection
//
// Pattern: overnight refreeze + gentle midday warming + no rain + adequate base.
// ---------------------------------------------------------------------------

export function detectCorn(
  lookback: LookbackHour[],
  snowDepthM: number | null,
  lapseAdjC: number,
): boolean {
  if (snowDepthM !== null && snowDepthM < 0.30) return false;

  const hasRain = lookback.some((h) => (h.w.rain_mm ?? 0) > 0.1);
  if (hasRain) return false;

  const overnightHours = lookback.filter((h) => {
    const hourOfDay = h.hoursFromStart % 24;
    return hourOfDay >= 22 || hourOfDay <= 7;
  });
  if (overnightHours.length === 0) return false;
  const minOvernight = Math.min(
    ...overnightHours.map((h) => h.w.temp_c - lapseAdjC),
  );
  if (minOvernight >= -5) return false;

  const currentMidday = lookback.filter((h) => {
    const hourOfDay = h.hoursFromStart % 24;
    return h.hoursFromStart >= 24 && hourOfDay >= 10 && hourOfDay <= 14;
  });
  if (currentMidday.length === 0) return false;
  const maxMidday = Math.max(
    ...currentMidday.map((h) => h.w.temp_c - lapseAdjC),
  );
  return maxMidday >= 1 && maxMidday <= 6;
}

// ---------------------------------------------------------------------------
// Breakable Crust Detection
//
// Detects melt-freeze cycles without subsequent fresh snow coverage.
// Breakable crust forms when daytime warming melts the snow surface and
// overnight refreezing creates a hard layer that hasn't been buried by
// fresh snowfall. Severity scales with the temperature swing across 0°C.
// ---------------------------------------------------------------------------

export function detectBreakableCrust(
  lookback: LookbackHour[],
  snowDepthM: number | null,
  cornDetected: boolean,
  lapseAdjC: number,
): CrustRisk {
  const noCrust: CrustRisk = {
    detected: false,
    severity: 0,
    label: null,
    detail: null,
    maxDaytimeTempC: null,
    minOvernightTempC: null,
    hoursSinceLastCoverage: null,
  };

  if (snowDepthM !== null && snowDepthM < 0.20) return noCrust;
  if (cornDetected) return noCrust;

  const lastHourIdx = lookback.length - 1;
  if (lastHourIdx < 12) return noCrust;

  let lastCoverageIdx = -1;
  let recentSnowAccumCm = 0;
  for (let i = lastHourIdx; i >= 0; i--) {
    recentSnowAccumCm += lookback[i].w.snowfall_cm ?? 0;
    if (recentSnowAccumCm >= CRUST_COVERAGE_THRESHOLD_CM) {
      lastCoverageIdx = i;
      break;
    }
  }

  const searchStart = lastCoverageIdx >= 0 ? lastCoverageIdx + 1 : 0;
  if (searchStart >= lastHourIdx - 6) return noCrust;

  let maxDaytimeTemp = -Infinity;
  let minOvernightTemp = Infinity;
  let hadDaytimeAboveZero = false;
  let hadOvernightBelowZero = false;

  for (let i = searchStart; i <= lastHourIdx; i++) {
    const h = lookback[i];
    const hourOfDay = h.hoursFromStart % 24;
    const adjTemp = h.w.temp_c - lapseAdjC;

    const isDaytime = hourOfDay >= 9 && hourOfDay <= 16;
    const isOvernight = hourOfDay >= 21 || hourOfDay <= 6;

    if (isDaytime) {
      if (adjTemp > maxDaytimeTemp) maxDaytimeTemp = adjTemp;
      if (adjTemp > 0) hadDaytimeAboveZero = true;
    }
    if (isOvernight) {
      if (adjTemp < minOvernightTemp) minOvernightTemp = adjTemp;
      if (adjTemp < 0) hadOvernightBelowZero = true;
    }
  }

  if (!hadDaytimeAboveZero || !hadOvernightBelowZero) return noCrust;

  const swing = maxDaytimeTemp - minOvernightTemp;
  if (swing < 3) return noCrust;

  const hoursSinceCoverage = lastCoverageIdx >= 0
    ? lookback[lastHourIdx].hoursFromStart - lookback[lastCoverageIdx].hoursFromStart
    : null;

  const rawSeverity = lerp(swing, 3, 12, 0.2, 1.0);
  const ageFactor = hoursSinceCoverage !== null
    ? Math.min(1.0, hoursSinceCoverage / 48)
    : 1.0;
  const severity = clamp(rawSeverity * ageFactor, 0, 1);

  let label: string;
  let detail: string;
  if (severity >= 0.7) {
    label = "High crust risk";
    detail = `Hard breakable crust likely. ${maxDaytimeTemp > 0 ? `Temps reached +${maxDaytimeTemp.toFixed(0)}°C` : ""} then refroze to ${minOvernightTemp.toFixed(0)}°C overnight. No fresh snow cover.`;
  } else if (severity >= 0.4) {
    label = "Moderate crust risk";
    detail = `Melt-freeze crust forming. Surface crossed 0°C with ${swing.toFixed(0)}°C temperature swing. Firm underfoot.`;
  } else {
    label = "Mild crust risk";
    detail = "Shallow melt-freeze cycle detected. Thin crust possible on sun-exposed aspects.";
  }

  return {
    detected: true,
    severity: round2(severity),
    label,
    detail,
    maxDaytimeTempC: round1(maxDaytimeTemp),
    minOvernightTempC: round1(minOvernightTemp),
    hoursSinceLastCoverage: hoursSinceCoverage,
  };
}
